import { type SyntheticEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/shared/forms';
import { Button } from '@/shared/ui';

import styles from './Kanban.module.css';

interface InlineFormProps {
  label: string;
  submitLabel: string;
  initialValue?: string;
  maxLength: number;
  pending?: boolean;
  /** Keep the form open after submit (quick consecutive adds). */
  keepOpen?: boolean;
  onSubmit: (value: string) => Promise<unknown>;
  onCancel: () => void;
}

/** Single-field form used for "add card", "add column" and renames. */
export function InlineForm({
  label,
  submitLabel,
  initialValue = '',
  maxLength,
  pending = false,
  keepOpen = false,
  onSubmit,
  onCancel,
}: InlineFormProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<unknown>(null);
  // A request in flight: a second Enter / click must not create a second card.
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    onSubmit(trimmed).then(
      () => {
        inFlight.current = false;
        setBusy(false);
        if (keepOpen) setValue('');
      },
      (cause: unknown) => {
        inFlight.current = false;
        setBusy(false);
        setError(cause);
      },
    );
  };

  return (
    <form className={styles.inlineForm} onSubmit={submit}>
      <input
        className={styles.inlineInput}
        aria-label={label}
        placeholder={label}
        value={value}
        maxLength={maxLength}
        autoFocus
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
      />
      <FormError error={error} />
      <div className={styles.inlineActions}>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={pending || busy || !value.trim()}
        >
          {submitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
